import { paletteFor } from "@app/core";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useReducedMotion } from "../../lib/motion/index.js";
import {
  clearCompletionPointer,
  rememberCompletionPointer,
  useCompletionCelebrationStore,
} from "../../stores/completionCelebration.store.js";
import { useThemeStore } from "../../stores/theme.store.js";
import { FireworksScene } from "./fireworks.js";

export function CompletionFireworks() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<FireworksScene | null>(null);
  const animationRef = useRef<number | null>(null);
  const lastLaunchRef = useRef(0);
  const launches = useCompletionCelebrationStore((state) => state.launches);
  const themeConfig = useThemeStore((state) => state.config);
  const palette = useMemo(() => paletteFor(themeConfig), [themeConfig]);
  const paletteRef = useRef(palette);
  paletteRef.current = palette;
  const reduceMotion = useReducedMotion();

  const startAnimation = useCallback(() => {
    if (animationRef.current != null) return;
    animationRef.current = requestAnimationFrame(function frame(now) {
      if (sceneRef.current?.step(now)) {
        animationRef.current = requestAnimationFrame(frame);
      } else {
        animationRef.current = null;
      }
    });
  }, []);

  useEffect(() => {
    const rememberPointer = (event: PointerEvent) => {
      rememberCompletionPointer({ x: event.clientX, y: event.clientY });
    };
    window.addEventListener("pointerdown", rememberPointer, true);
    window.addEventListener("keydown", clearCompletionPointer, true);
    return () => {
      window.removeEventListener("pointerdown", rememberPointer, true);
      window.removeEventListener("keydown", clearCompletionPointer, true);
      clearCompletionPointer();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const scene = new FireworksScene(canvas, ctx, paletteRef.current);
    sceneRef.current = scene;
    const resize = () => scene.resize();
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (animationRef.current != null) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      scene.clear();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    sceneRef.current?.setPalette(palette);
  }, [palette]);

  useEffect(() => {
    const pending = launches.filter((launch) => launch.id > lastLaunchRef.current);
    if (pending.length === 0) return;
    lastLaunchRef.current = pending[pending.length - 1]!.id;
    if (reduceMotion) return;
    const now = performance.now();
    pending.forEach((launch, i) => {
      sceneRef.current?.launch(now + i * 120, 3, launch.origin);
    });
    startAnimation();
  }, [launches, reduceMotion, startAnimation]);

  useEffect(() => {
    if (!reduceMotion) return;
    if (animationRef.current != null) cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
    sceneRef.current?.clear();
  }, [reduceMotion]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[100] h-full w-full"
    />
  );
}
